import { z } from 'zod'

import { InvoiceSchema, InvoiceStatusEnum } from './invoice'
import { createPaginatedResponseSchema } from './pagination'
import { decimalSchema } from './prisma-zod'

export const BillingScheduleLineStatusSchema = z.enum([
  'PLANNED',
  'READY',
  'INVOICED',
  'SKIPPED',
  'CANCELLED',
])

export type BillingScheduleLineStatus = z.infer<
  typeof BillingScheduleLineStatusSchema
>

export const BillingScheduleLineStatusLabels: Record<
  BillingScheduleLineStatus,
  string
> = {
  PLANNED: 'Planned',
  READY: 'Ready',
  INVOICED: 'Invoiced',
  SKIPPED: 'Skipped',
  CANCELLED: 'Canceled',
}

export const BillingScheduleCadenceSchema = z.enum([
  'ONE_TIME',
  'MONTHLY',
  'QUARTERLY',
  'ANNUAL',
])

export type BillingScheduleCadence = z.infer<
  typeof BillingScheduleCadenceSchema
>

export const BillingScheduleCadenceLabels: Record<
  BillingScheduleCadence,
  string
> = {
  ONE_TIME: 'One-time',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
}

export const BillingScheduleLineSourceSchema = z.enum([
  'MANUAL',
  'CONTRACT_TERMS',
])

export type BillingScheduleLineSource = z.infer<
  typeof BillingScheduleLineSourceSchema
>

export const BillingScheduleCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isDefault: z.boolean().default(false),
  sortOrder: z.number().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type BillingScheduleCategory = z.infer<
  typeof BillingScheduleCategorySchema
>

export const UpsertBillingScheduleCategorySchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().nullable().optional(),
  isDefault: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
})

export type UpsertBillingScheduleCategory = z.infer<
  typeof UpsertBillingScheduleCategorySchema
>

export const UpdateBillingScheduleCategorySchema =
  UpsertBillingScheduleCategorySchema.partial()

export type UpdateBillingScheduleCategory = z.infer<
  typeof UpdateBillingScheduleCategorySchema
>

export const BillingScheduleLineSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  billingAuthorizationId: z.string().uuid().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  description: z.string(),
  amount: decimalSchema,
  currency: z.string().default('USD'),
  quantity: decimalSchema.nullable().optional(),
  unitPrice: decimalSchema.nullable().optional(),
  billingDate: z.date().nullable().optional(),
  billingMonth: z.date().nullable().optional(),
  cadence: BillingScheduleCadenceSchema.default('ONE_TIME'),
  status: BillingScheduleLineStatusSchema.default('PLANNED'),
  sourceType: BillingScheduleLineSourceSchema.default('MANUAL'),
  periodIndex: z.number().int().nullable().optional(),
  invoiceId: z.string().uuid().nullable().optional(),
  invoiceItemId: z.string().uuid().nullable().optional(),
  invoice: z
    .object({
      id: z.string().uuid(),
      invoiceNumber: z.string().nullable().optional(),
      title: z.string().nullable().optional(),
      status: z.nativeEnum(InvoiceStatusEnum).nullable().optional(),
    })
    .nullable()
    .optional(),
  notes: z.string().nullable().optional(),
  // Slim client reference for cross-client surfaces; the full record stays
  // behind the entities API.
  entity: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  // Set when a user directly edited the amount of a CONTRACT_TERMS line;
  // regeneration only overwrites such lines with explicit opt-in.
  amountEditedAt: z.date().nullable().optional(),
  category: BillingScheduleCategorySchema.nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type BillingScheduleLine = z.infer<typeof BillingScheduleLineSchema>

export const BillingScheduleLinesResponseSchema = createPaginatedResponseSchema(
  BillingScheduleLineSchema,
)

export type BillingScheduleLinesResponse = z.infer<
  typeof BillingScheduleLinesResponseSchema
>

export const UpsertBillingScheduleLineSchema = z.object({
  entityId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  billingAuthorizationId: z.string().uuid().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  description: z.string().trim().min(1),
  amount: decimalSchema.refine(value => Number(value) >= 0, {
    message: 'Amount must be non-negative',
  }),
  currency: z.string().trim().min(3).max(3).default('USD'),
  quantity: decimalSchema
    .nullable()
    .optional()
    .refine(value => value == null || Number(value) >= 0, {
      message: 'Quantity must be non-negative',
    }),
  unitPrice: decimalSchema
    .nullable()
    .optional()
    .refine(value => value == null || Number(value) >= 0, {
      message: 'Unit price must be non-negative',
    }),
  billingDate: z.coerce.date().nullable().optional(),
  billingMonth: z.coerce.date().nullable().optional(),
  cadence: BillingScheduleCadenceSchema.default('ONE_TIME'),
  status: BillingScheduleLineStatusSchema.default('PLANNED'),
  notes: z.string().nullable().optional(),
})

export type UpsertBillingScheduleLine = z.infer<
  typeof UpsertBillingScheduleLineSchema
>

export const UpdateBillingScheduleLineSchema =
  UpsertBillingScheduleLineSchema.partial()

export type UpdateBillingScheduleLine = z.infer<
  typeof UpdateBillingScheduleLineSchema
>

export const BillingScheduleLineFiltersSchema = z.object({
  entityId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  contractId: z.string().uuid().optional(),
  billingAuthorizationId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  status: z
    .union([
      BillingScheduleLineStatusSchema,
      z.array(BillingScheduleLineStatusSchema),
    ])
    .optional(),
  billingMonth: z.coerce.date().optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().optional(),
})

export type BillingScheduleLineFilters = z.infer<
  typeof BillingScheduleLineFiltersSchema
>

export const GenerateBillingScheduleInvoiceGroupingSchema = z.enum([
  'single_invoice',
  'by_project',
  'by_contract',
  'by_authorization',
])

export type GenerateBillingScheduleInvoiceGrouping = z.infer<
  typeof GenerateBillingScheduleInvoiceGroupingSchema
>

export const GenerateBillingScheduleInvoiceSchema = z.object({
  lineIds: z.array(z.string().uuid()).min(1),
  invoiceDate: z.coerce.date(),
  // Optional: when absent the API derives it from the resolved payment terms
  // (billing profile, then org default) via parsePaymentTermsDays.
  dueDate: z.coerce.date().optional(),
  grouping:
    GenerateBillingScheduleInvoiceGroupingSchema.default('single_invoice'),
  billingAuthorizationId: z.string().uuid().nullable().optional(),
  // Invoicing-ops Phase 2 (A11): explicit billing-profile pick for the
  // generated invoices. Absent/null means the entity's default profile.
  billingProfileId: z.string().uuid().nullable().optional(),
})

export type GenerateBillingScheduleInvoice = z.infer<
  typeof GenerateBillingScheduleInvoiceSchema
>

export const GenerateBillingScheduleInvoiceResultSchema = z.object({
  invoices: z.array(InvoiceSchema),
  updatedLineIds: z.array(z.string().uuid()),
})

export type GenerateBillingScheduleInvoiceResult = z.infer<
  typeof GenerateBillingScheduleInvoiceResultSchema
>

// --- Generate recurring schedule lines from a contract's structured terms ---

export const GenerateContractBillingScheduleSchema = z.object({
  // false = dry-run preview (no writes); true = apply the reconciled changes.
  commit: z.boolean().default(false),
  // Hand-edited lines (amountEditedAt set) whose computed amount differs are
  // only replaced when confirmOverwrites is true AND their id is listed in
  // overwriteLineIds; otherwise they are kept as-is.
  confirmOverwrites: z.boolean().default(false),
  overwriteLineIds: z.array(z.string().uuid()).optional(),
})

export type GenerateContractBillingSchedule = z.infer<
  typeof GenerateContractBillingScheduleSchema
>

export const ContractBillingScheduleActionSchema = z.enum([
  'create', // new line for a period with no existing coverage
  'replace', // editable generated line at this period gets soft-deleted + recreated
  'keep', // existing invoiced/skipped/canceled line preserved as-is; proposed line skipped
  'remove', // editable generated line whose period dropped out of the new series
  'overwrite', // hand-edited line whose computed amount differs; replaced only on explicit opt-in
])

export type ContractBillingScheduleAction = z.infer<
  typeof ContractBillingScheduleActionSchema
>

export const ContractBillingSchedulePreviewLineSchema = z.object({
  action: ContractBillingScheduleActionSchema,
  periodIndex: z.number().int(),
  description: z.string(),
  amount: decimalSchema,
  currency: z.string(),
  billingDate: z.date().nullable().optional(),
  billingMonth: z.date().nullable().optional(),
  cadence: BillingScheduleCadenceSchema,
  existingLineId: z.string().uuid().nullable().optional(),
  existingStatus: BillingScheduleLineStatusSchema.nullable().optional(),
  // For 'overwrite' rows: the line's current hand-edited amount (the proposed
  // computed amount is in `amount`).
  existingAmount: decimalSchema.nullable().optional(),
  amountEditedAt: z.date().nullable().optional(),
})

export type ContractBillingSchedulePreviewLine = z.infer<
  typeof ContractBillingSchedulePreviewLineSchema
>

export const ContractBillingSchedulePreviewSchema = z.object({
  contractId: z.string().uuid(),
  committed: z.boolean(),
  cadence: BillingScheduleCadenceSchema,
  count: z.number().int(),
  lines: z.array(ContractBillingSchedulePreviewLineSchema),
  summary: z.object({
    toCreate: z.number().int(),
    toReplace: z.number().int(),
    kept: z.number().int(),
    removed: z.number().int(),
    alreadyInvoiced: z.number().int(),
    // Proposed overwrites of hand-edited amounts; on a commit without the
    // opt-in these lines are kept instead.
    toOverwrite: z.number().int(),
  }),
})

export type ContractBillingSchedulePreview = z.infer<
  typeof ContractBillingSchedulePreviewSchema
>
